import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Plus, ShieldAlert, CheckCircle2, X } from "lucide-react";
import { SectionCard } from "@/components/common/SectionCard";
import { DataTable } from "@/components/common/DataTable";
import { EntryModal } from "@/components/common/EntryModal";
import { ConfirmDialog } from "@/components/common/ConfirmDialog";
import { StatusBadge } from "@/components/common/StatusBadge";
import { RowActions } from "./RowActions";
import { Button } from "@/components/ui/button";
import { useCrud } from "@/hooks/useCrud";
import { useUser } from "@/context/UserContext";
import {
  OWNERS,
  AREAS,
  BLOCKER_SEVERITY,
  BLOCKER_STATUS,
  priorityBadge,
  statusBadge,
} from "@/data/commandOptions";

const today = () => new Date().toISOString().slice(0, 10);

const FIELDS = [
  { name: "title", label: "Blocker title", type: "text", placeholder: "e.g. App Store review delay", required: true },
  { name: "description", label: "Description", type: "textarea", placeholder: "What's blocked and why?" },
  { name: "area", label: "Area", type: "select", placeholder: "Area", options: AREAS, required: true },
  { name: "owner", label: "Owner", type: "select", placeholder: "Owner", options: OWNERS, required: true },
  { name: "severity", label: "Severity", type: "select", placeholder: "Severity", options: BLOCKER_SEVERITY, required: true },
  { name: "status", label: "Status", type: "select", placeholder: "Status", options: BLOCKER_STATUS, required: true },
  { name: "date_raised", label: "Date raised", type: "date" },
  { name: "target_resolution_date", label: "Target resolution date", type: "date" },
  { name: "latest_update", label: "Latest update", type: "textarea", placeholder: "Most recent note" },
  { name: "resolution_note", label: "Resolution note", type: "textarea", placeholder: "How it was resolved" },
];

export const BlockersPanel = ({ filter }) => {
  const { items, save, remove, patch, loading } = useCrud("blockers", "Blocker");
  const { canWrite } = useUser();
  const navigate = useNavigate();
  const [sp, setSp] = useSearchParams();
  const openId = sp.get("blocker");
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [confirmId, setConfirmId] = useState(null);

  const recordTo = (id) => { const p = new URLSearchParams(sp); p.set("blocker", id); return `?${p.toString()}`; };
  const closeRecord = useCallback(() => { const p = new URLSearchParams(sp); p.delete("blocker"); setSp(p); }, [sp, setSp]);

  const openAdd = () => { setEditing({ owner: "Steven", area: "General", severity: "Medium", status: "Open", date_raised: today() }); setModalOpen(true); };
  const openEdit = (row) => { setEditing(row); setModalOpen(true); };

  const openedRef = useRef(null);
  useEffect(() => {
    if (!openId) { openedRef.current = null; return; }
    if (loading || openedRef.current === openId) return;
    openedRef.current = openId;
    const b = items.find((x) => x.id === openId);
    if (b && canWrite) { openEdit(b); }
    else { if (!b) toast.error("That blocker is unavailable — it may have been deleted."); closeRecord(); }
  }, [openId, loading, items, canWrite, closeRecord]);

  let rows = items;
  if (filter === "critical") rows = items.filter((b) => b.severity === "Critical" && b.status !== "Resolved");
  else if (filter === "open") rows = items.filter((b) => b.status !== "Resolved");
  const bannerLabel = filter === "critical" ? "Filtered to critical, unresolved blockers" : filter === "open" ? "Filtered to open blockers" : null;

  const columns = [
    { key: "title", header: "Blocker", render: (r) => (
      <div>
        {canWrite
          ? <Link to={recordTo(r.id)} data-testid={`open-blocker-${r.id}`} className="font-medium text-[#0D1B2A] hover:text-[#FB6F5A]">{r.title}</Link>
          : <p className="font-medium text-[#0D1B2A]">{r.title}</p>}
        <p className="mt-0.5 text-xs text-[#5C6B7B]">{r.area}{r.target_resolution_date ? ` · target ${r.target_resolution_date}` : ""}</p>
      </div>
    )},
    { key: "owner", header: "Owner" },
    { key: "severity", header: "Severity", render: (r) => <StatusBadge status={priorityBadge(r.severity)} label={r.severity} /> },
    { key: "status", header: "Status", render: (r) => <StatusBadge status={statusBadge(r.status)} label={r.status} /> },
    { key: "actions", header: "", thClassName: "text-right", className: "text-right", render: (r) => (
      <RowActions idx={r.id} onEdit={() => openEdit(r)} onDelete={() => setConfirmId(r.id)}
        extra={r.status !== "Resolved" && (
          <Button size="sm" variant="ghost" data-testid={`resolve-${r.id}`}
            onClick={() => patch(r.id, { status: "Resolved" }, "Blocker resolved")}
            className="h-8 gap-1 text-[#2D6A4F] hover:bg-[#2D6A4F]/10">
            <CheckCircle2 size={15} /> Resolve
          </Button>
        )}
      />
    )},
  ];

  return (
    <SectionCard
      testid="blockers-card"
      accent="#FB6F5A"
      eyebrow="Expedition Obstacles"
      icon={ShieldAlert}
      title="Blockers"
      subtitle="Obstacles in the expedition path — high and critical ones surface on the overview."
      action={canWrite ? <Button size="sm" data-testid="add-blocker-btn" onClick={openAdd} className="rounded-full bg-[#FB6F5A] text-white hover:bg-[#EF5741]"><Plus size={15} /> Add</Button> : null}
    >
      {bannerLabel && (
        <div data-testid="blockers-filter-banner" className="mb-3 flex items-center justify-between rounded-xl bg-[#FB6F5A]/10 px-3 py-2 text-xs font-semibold text-[#B91C1C]">
          <span>{bannerLabel}</span>
          <button data-testid="clear-filter" onClick={() => navigate("/?tab=blockers")} className="inline-flex items-center gap-1 underline hover:no-underline"><X size={13} /> Clear</button>
        </div>
      )}
      <DataTable
        testid="blockers-table"
        columns={columns}
        rows={rows}
        searchKeys={["title", "owner", "area", "description"]}
        searchPlaceholder="Search blockers…"
        filters={[
          { key: "area", label: "Area", options: AREAS },
          { key: "owner", label: "Owner", options: OWNERS },
          { key: "severity", label: "Severity", options: BLOCKER_SEVERITY },
          { key: "status", label: "Status", options: BLOCKER_STATUS },
        ]}
        emptyIcon={ShieldAlert}
        emptyImage={bannerLabel ? undefined : "/commando-mascot-v2.webp"}
        emptyTitle={bannerLabel ? "No matching blockers" : "No blockers logged"}
        emptyDescription={bannerLabel ? "Nothing matches this filter right now." : "Record blockers early so they can be actively managed."}
        emptyActionLabel={bannerLabel || !canWrite ? undefined : "Add blocker"}
        onEmptyAction={bannerLabel || !canWrite ? undefined : openAdd}
      />
      <EntryModal open={modalOpen} onOpenChange={(o) => { setModalOpen(o); if (!o) closeRecord(); }}
        title={editing?.id ? "Edit Blocker" : "Add Blocker"}
        submitLabel={editing?.id ? "Save Changes" : "Add Blocker"}
        fields={FIELDS} initialValues={editing} onSubmit={save} />
      <ConfirmDialog open={!!confirmId} onOpenChange={(o) => !o && setConfirmId(null)}
        title="Delete this blocker?" description="This blocker will be permanently removed."
        onConfirm={() => { remove(confirmId); setConfirmId(null); }} />
    </SectionCard>
  );
};
